<template>
	<div id="userManage">
		<page-header title="用户管理" :count="total">
			<template #actions>
				<el-button v-if="hasPermission('USER_CREATE')" type="primary" size="small"
					icon="el-icon-plus" @click="showAddDialog">添加用户</el-button>
			</template>
			<el-select v-model="filterType" placeholder="全部角色" clearable size="small" @change="loadUsers">
				<el-option v-for="(label, code) in roleLabels" :key="code" :label="label" :value="code" />
			</el-select>
			<el-input v-model="searchPhone" class="ph-search" placeholder="搜索手机号" clearable size="small"
				prefix-icon="el-icon-search"
				@clear="loadUsers" @keyup.enter.native="searchByPhone" />
			<el-button size="small" type="primary" plain @click="searchByPhone">搜索</el-button>
		</page-header>

		<!-- 用户列表 -->
		<div class="user-table" v-loading="loading">
			<el-table :data="users" border stripe size="small" class="app-table"
				:header-cell-style="{ background: '#f5f7fa', color: '#1a1a2e', fontWeight: 700 }">
				<el-table-column label="姓名" prop="realName" width="120">
					<template slot-scope="{ row }">
						<div class="user-cell">
							<div class="avatar-sm">{{ (row.realName || 'U').slice(-1) }}</div>
							<span>{{ row.realName || '-' }}</span>
						</div>
					</template>
				</el-table-column>
				<el-table-column label="手机号" prop="phone" width="140">
					<template slot-scope="{ row }">
						<span class="mono">{{ row.phone || '-' }}</span>
					</template>
				</el-table-column>
				<el-table-column label="角色" prop="userType" width="120">
					<template slot-scope="{ row }">
						<el-tag :type="roleTagType(row.userType)" size="mini">
							{{ roleLabels[row.userType] || row.userType }}
						</el-tag>
					</template>
				</el-table-column>
				<el-table-column label="科室/部门" prop="department" min-width="140">
					<template slot-scope="{ row }">{{ row.department || '-' }}</template>
				</el-table-column>
				<el-table-column label="医院/机构" prop="hospitalName" min-width="160">
					<template slot-scope="{ row }">{{ row.hospitalName || '-' }}</template>
				</el-table-column>
				<el-table-column label="状态" prop="status" width="80" align="center">
					<template slot-scope="{ row }">
						<el-tag :type="row.status === 1 ? 'success' : 'danger'" size="mini">
							{{ row.status === 1 ? '正常' : '已禁用' }}
						</el-tag>
					</template>
				</el-table-column>
				<el-table-column label="创建时间" prop="createTime" width="160">
					<template slot-scope="{ row }">
						<span class="time-text">{{ formatTime(row.createTime) }}</span>
					</template>
				</el-table-column>
				<el-table-column label="操作" width="180" align="center" fixed="right">
					<template slot-scope="{ row }">
						<el-button v-if="hasPermission('USER_EDIT') && canEdit(row)" type="text" size="mini"
							@click="openEditDialog(row)">编辑</el-button>
						<el-button v-if="hasPermission('USER_RESET_PWD') && canEdit(row)" type="text" size="mini"
							@click="resetPassword(row)" style="color:#E6A23C;">重置</el-button>
						<el-button v-if="hasPermission('USER_DISABLE') && row.status === 1 && row.userType !== 'ADMIN'"
							type="text" size="mini" @click="disableUser(row)" style="color:#e74c3c;">禁用</el-button>
					</template>
				</el-table-column>
			</el-table>

			<!-- 分页 -->
			<div class="app-pagination" v-if="total > pageSize">
				<el-pagination background layout="prev, pager, next, total" :total="total"
					:page-size="pageSize" :current-page.sync="currentPage"
					@current-change="loadUsers" />
			</div>

			<!-- 空状态 -->
			<div class="empty-tip" v-if="!loading && users.length === 0">
				<i class="el-icon-user"></i>
				<p>暂无用户数据</p>
			</div>
		</div>

		<!-- 添加用户弹窗 -->
		<el-dialog title="添加用户" :visible.sync="addDialogVisible" width="520px"
			:close-on-click-modal="false" custom-class="compact-form-dialog">
			<el-form :model="addForm" :rules="addRules" ref="addForm" label-position="right"
				label-width="100px" size="small">
				<el-form-item label="手机号" prop="phone">
					<el-input v-model="addForm.phone" placeholder="请输入手机号" maxlength="11"
						prefix-icon="el-icon-phone" />
				</el-form-item>
				<el-form-item label="姓名" prop="realName">
					<el-input v-model="addForm.realName" placeholder="请输入用户姓名"
						prefix-icon="el-icon-user" />
				</el-form-item>
				<el-form-item label="角色" prop="userType">
					<el-select v-model="addForm.userType" placeholder="请选择角色" style="width: 100%;">
						<el-option v-for="(label, code) in assignableRoles" :key="code" :label="label" :value="code" />
					</el-select>
				</el-form-item>
				<el-form-item label="科室/部门">
					<el-input v-model="addForm.department" placeholder="选填" />
				</el-form-item>
				<el-form-item label="医院/机构">
					<el-input v-model="addForm.hospitalName" placeholder="选填" />
				</el-form-item>
			</el-form>
			<div class="dialog-tip">
				<i class="el-icon-info"></i>
				默认密码为手机号后 6 位，用户登录后可自行修改
			</div>
			<span slot="footer">
				<el-button @click="addDialogVisible = false">取消</el-button>
				<el-button type="primary" :loading="addLoading" @click="submitAddUser">确认添加</el-button>
			</span>
		</el-dialog>

		<!-- 编辑用户弹窗 -->
		<el-dialog title="编辑用户" :visible.sync="editDialogVisible" width="520px"
			:close-on-click-modal="false" custom-class="compact-form-dialog">
			<el-form :model="editForm" :rules="editRules" ref="editForm" label-position="right"
				label-width="100px" size="small">
				<el-form-item label="姓名" prop="realName">
					<el-input v-model="editForm.realName" placeholder="请输入姓名" />
				</el-form-item>
				<el-form-item label="手机号" prop="phone">
					<el-input v-model="editForm.phone" maxlength="11" placeholder="登录使用" />
				</el-form-item>
				<el-form-item label="角色" prop="userType">
					<el-select v-model="editForm.userType" style="width: 100%;">
						<el-option v-for="(label, code) in assignableRoles" :key="code" :label="label" :value="code" />
					</el-select>
				</el-form-item>
				<el-form-item label="职称">
					<el-input v-model="editForm.title" placeholder="选填" />
				</el-form-item>
				<el-form-item label="科室/部门">
					<el-input v-model="editForm.department" placeholder="选填" />
				</el-form-item>
				<el-form-item label="医院/机构">
					<el-input v-model="editForm.hospitalName" placeholder="选填" />
				</el-form-item>
				<el-form-item label="邮箱">
					<el-input v-model="editForm.email" placeholder="选填" />
				</el-form-item>
				<el-form-item label="状态">
					<el-radio-group v-model="editForm.status">
						<el-radio :label="1">正常</el-radio>
						<el-radio :label="0">禁用</el-radio>
					</el-radio-group>
				</el-form-item>
			</el-form>
			<span slot="footer">
				<el-button @click="editDialogVisible = false">取消</el-button>
				<el-button type="primary" :loading="editLoading" @click="submitEditUser">保 存</el-button>
			</span>
		</el-dialog>
	</div>
</template>

<script>
import service from '@/request/request.js';
import PageHeader from '@/components/PageHeader.vue';

export default {
	name: 'UserManage',
	components: { PageHeader },
	data() {
		return {
			loading: false,
			users: [],
			total: 0,
			currentPage: 1,
			pageSize: 10,
			filterType: '',
			searchPhone: '',
			// 添加用户
			addDialogVisible: false,
			addLoading: false,
			addForm: {
				phone: '',
				realName: '',
				userType: 'DOCTOR',
				department: '',
				hospitalName: ''
			},
			addRules: {
				phone: [
					{ required: true, message: '请输入手机号', trigger: 'blur' },
					{ pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号', trigger: 'blur' }
				],
				realName: [
					{ required: true, message: '请输入姓名', trigger: 'blur' }
				],
				userType: [
					{ required: true, message: '请选择角色', trigger: 'change' }
				]
			},
			// 角色标签 — 与后端 PermissionService.ALL_ROLES 一致 (7 个权威角色)
			roleLabels: {
				ADMIN: '管理员',
				ASSISTANT: '助教',
				OFFICIAL_PROJECT: '项目官员',
				OFFICIAL_SETTLEMENT: '结算官员',
				OFFICIAL_AUDIT: '审核官员',
				SPONSOR: '企业人员',
				DOCTOR: '医生',
				// 兼容历史数据: 老的合并 OFFICIAL 仍可展示, 但新建/编辑不可选
				OFFICIAL: '官员(旧)'
			},
			// 当前用户权限
			permissions: [],
			// 当前登录角色, 用于判断能否编辑别人
			currentRole: '',
			// 编辑用户
			editDialogVisible: false,
			editLoading: false,
			editForm: {
				id: null, realName: '', phone: '', email: '', userType: '',
				department: '', hospitalName: '', title: '', status: 1
			},
			editRules: {
				realName: [{ required: true, message: '请输入姓名', trigger: 'blur' }],
				phone: [
					{ required: true, message: '请输入手机号', trigger: 'blur' },
					{ pattern: /^1[3-9]\d{9}$/, message: '手机号格式错误', trigger: 'blur' }
				],
				userType: [{ required: true, message: '请选择角色', trigger: 'change' }]
			}
		}
	},
	computed: {
		/** 用户可在前端选择分配的角色 (排除 ADMIN, 排除已废弃的 OFFICIAL) */
		assignableRoles() {
			const all = this.roleLabels;
			const result = {};
			Object.keys(all).forEach(code => {
				if (code !== 'ADMIN' && code !== 'OFFICIAL') result[code] = all[code];
			});
			return result;
		}
	},
	created() {
		const token = JSON.parse(localStorage.getItem('token') || '{}');
		this.permissions = token.permissions || [];
		this.currentRole = token.type || '';
		// ADMIN 始终有全部权限
		if (token.type === 'ADMIN') {
			this.permissions = ['USER_VIEW', 'USER_CREATE', 'USER_EDIT', 'USER_RESET_PWD', 'USER_DISABLE',
				'MEETING_CREATE', 'MEETING_MANAGE', 'MEETING_END', 'SPONSOR_MANAGE', 'DOCTOR_MANAGE'];
		}
		this.loadUsers();
	},
	methods: {
		hasPermission(perm) {
			return this.permissions.includes(perm);
		},

		roleTagType(type) {
			const map = {
				ADMIN: 'danger',
				ASSISTANT: '',
				OFFICIAL_PROJECT: 'warning',
				OFFICIAL_SETTLEMENT: 'warning',
				OFFICIAL_AUDIT: 'warning',
				SPONSOR: 'info',
				DOCTOR: 'success',
				OFFICIAL: 'warning'   // 旧数据兼容
			};
			return map[type] || 'info';
		},

		formatTime(t) {
			if (!t) return '-';
			return this.$moment ? this.$moment(t).format('YYYY-MM-DD HH:mm') : t;
		},

		/** 加载用户列表 */
		async loadUsers() {
			this.loading = true;
			try {
				const params = { page: this.currentPage, size: this.pageSize };
				if (this.filterType) params.userType = this.filterType;
				const res = await service.get('/api/user/list', { params });
				if (res.code === 200) {
					this.users = res.data.records || [];
					this.total = res.data.total || 0;
				}
			} catch (err) {
				console.error('加载用户失败:', err);
			} finally {
				this.loading = false;
			}
		},

		/** 按手机号搜索 */
		async searchByPhone() {
			if (!this.searchPhone) {
				this.loadUsers();
				return;
			}
			this.loading = true;
			try {
				const res = await service.get(`/api/user/phone/${this.searchPhone}`);
				if (res.code === 200 && res.data) {
					this.users = [res.data];
					this.total = 1;
				} else {
					this.users = [];
					this.total = 0;
					this.$message.warning('未找到该手机号的用户');
				}
			} catch (err) {
				this.$message.error('搜索失败');
			} finally {
				this.loading = false;
			}
		},

		/** 打开添加弹窗 */
		showAddDialog() {
			this.addForm = { phone: '', realName: '', userType: 'DOCTOR', department: '', hospitalName: '' };
			this.addDialogVisible = true;
			this.$nextTick(() => {
				this.$refs.addForm && this.$refs.addForm.clearValidate();
			});
		},

		/** 提交添加 */
		submitAddUser() {
			this.$refs.addForm.validate(async valid => {
				if (!valid) return;
				this.addLoading = true;
				try {
					const res = await service.post('/api/user/create', {
						phone: this.addForm.phone,
						realName: this.addForm.realName,
						userType: this.addForm.userType,
						orgId: JSON.parse(localStorage.getItem('token') || '{}').orgId || 1
					});
					if (res.code === 200) {
						this.$message.success(`用户 ${this.addForm.realName} 创建成功，默认密码为手机号后6位`);
						this.addDialogVisible = false;
						this.loadUsers();
					} else {
						this.$message.error(res.msg || '创建失败');
					}
				} catch (err) {
					this.$message.error('创建失败');
				} finally {
					this.addLoading = false;
				}
			});
		},

		/** 禁用用户 */
		disableUser(user) {
			this.$confirm(`确定要禁用用户 ${user.realName}（${user.phone}）？禁用后该用户将无法登录系统。`, '禁用确认', {
				confirmButtonText: '确认禁用',
				cancelButtonText: '取消',
				type: 'warning'
			}).then(async () => {
				try {
					const res = await service.post(`/api/user/${user.id}/disable`);
					if (res.code === 200) {
						this.$message.success('用户已禁用');
						this.loadUsers();
					} else {
						this.$message.error(res.msg || '操作失败');
					}
				} catch (err) {
					this.$message.error('操作失败');
				}
			}).catch(() => {});
		},

		/** 当前登录者能否编辑指定用户 */
		canEdit(row) {
			if (!['ADMIN', 'ASSISTANT'].includes(this.currentRole)) return false;
			// 非 ADMIN 不能编辑 ADMIN 角色
			if (row.userType === 'ADMIN' && this.currentRole !== 'ADMIN') return false;
			return true;
		},

		/** 打开编辑用户弹窗 */
		openEditDialog(row) {
			this.editForm = {
				id: row.id,
				realName: row.realName || '',
				phone: row.phone || '',
				email: row.email || '',
				userType: row.userType || '',
				department: row.department || '',
				hospitalName: row.hospitalName || '',
				title: row.title || '',
				status: row.status != null ? row.status : 1
			};
			this.editDialogVisible = true;
			this.$nextTick(() => { this.$refs.editForm && this.$refs.editForm.clearValidate(); });
		},

		/** 提交编辑 */
		submitEditUser() {
			this.$refs.editForm.validate(async valid => {
				if (!valid) return;
				this.editLoading = true;
				try {
					const res = await service.post(`/api/user/${this.editForm.id}/admin-update`, {
						realName: this.editForm.realName,
						phone: this.editForm.phone,
						email: this.editForm.email || null,
						userType: this.editForm.userType,
						department: this.editForm.department || null,
						hospitalName: this.editForm.hospitalName || null,
						title: this.editForm.title || null,
						status: this.editForm.status
					});
					if (res.code === 200) {
						this.$message.success('修改成功');
						this.editDialogVisible = false;
						this.loadUsers();
					} else {
						this.$message.error(res.msg || '修改失败');
					}
				} catch (e) {
					this.$message.error('修改失败');
				}
				this.editLoading = false;
			});
		},

		/** 重置用户密码 */
		resetPassword(row) {
			this.$confirm(`确定将 ${row.realName}（${row.phone}）的密码重置为手机后 6 位？用户下次登录时将被强制修改。`, '重置密码', {
				confirmButtonText: '确认重置',
				cancelButtonText: '取消',
				type: 'warning'
			}).then(async () => {
				try {
					const res = await service.post(`/api/user/${row.id}/reset-password`);
					if (res.code === 200) {
						const pwd = res.data && res.data.defaultPassword;
						this.$alert(`密码已重置, 新密码: <b style="color:#E6A23C">${pwd}</b>(手机后 6 位)<br/>用户下次登录将被强制修改。`,
							'重置成功', { dangerouslyUseHTMLString: true, type: 'success' });
					} else {
						this.$message.error(res.msg || '重置失败');
					}
				} catch (e) {
					this.$message.error('重置失败');
				}
			}).catch(() => {});
		}
	}
}
</script>

<style lang="scss" scoped>
#userManage {
	padding: 32px 40px;
	background: #f0f2f8;
	min-height: 100vh;

	/* .page-header / .filter-bar 已统一到 PageHeader 组件 */

	.user-table {
		background: #fff;
		border-radius: 14px;
		padding: 20px 24px;
		box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);

		.user-cell {
			display: flex;
			align-items: center;
			gap: 8px;

			.avatar-sm {
				width: 28px;
				height: 28px;
				border-radius: 8px;
				background: linear-gradient(135deg, #667eea, #764ba2);
				color: #fff;
				display: flex;
				align-items: center;
				justify-content: center;
				font-size: 12px;
				font-weight: 700;
				flex-shrink: 0;
			}

			span {
				font-weight: 600;
				color: #1a1a2e;
			}
		}

		.mono {
			font-family: 'SF Mono', Consolas, monospace;
			font-size: 13px;
			color: #64748b;
		}

		.time-text {
			font-size: 12px;
			color: #8f9bb3;
		}

		.disabled-text {
			color: #ccc;
		}

		.pagination {
			display: flex;
			justify-content: center;
			padding-top: 16px;
		}

		.empty-tip {
			text-align: center;
			padding: 60px 20px;
			color: #a8a29e;

			i { font-size: 40px; display: block; margin-bottom: 10px; }
			p { font-size: 14px; margin: 0; }
		}
	}

	.dialog-tip {
		background: rgba(102, 126, 234, 0.06);
		color: #667eea;
		font-size: 12px;
		padding: 10px 14px;
		border-radius: 8px;
		margin-top: 8px;

		i { margin-right: 4px; }
	}
}
</style>
